import classNames from 'classnames';
import React, { useCallback, useContext } from 'react';
import { RadioContext } from './context';
import styles from './RadioButton.module.scss';

interface Props<T> extends Omit<React.HTMLProps<HTMLInputElement>, 'label' | 'value'> {
  hideRadio?: boolean;
  label?: string | JSX.Element;
  onChangeChecked?: (checked: boolean) => void;
  value: T;
}

export const RadioButton = <T,>({
  children,
  className,
  disabled,
  hideRadio,
  label,
  name,
  value,
  onChange,
  onChangeChecked,
  ...props
}: Props<T>) => {
  const context = useContext(RadioContext);
  const checked = context.selectedValue != null && context.selectedValue === value;

  const handleOnChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      onChange?.(event);
      onChangeChecked?.(target.checked);
      context.onChangeSelected?.(value);
    },
    [context, onChange, onChangeChecked, value]
  );

  const radioButton = !hideRadio ? (
    <span
      data-testid="radio-button"
      className={classNames(!label && className, styles.radio, {
        [styles.checked]: checked,
        [styles.disabled]: disabled,
      })}
      role="radio"
      aria-checked={checked}
    >
      <input
        type="radio"
        checked={checked}
        disabled={disabled}
        onChange={disabled ? undefined : handleOnChange}
        value={value as any}
        name={context.groupName ?? name}
        data-testid={`radio-${checked}`}
        {...props}
      />
      <span className={styles.inner} />
    </span>
  ) : null;

  const labelContent = children ?? label;
  return labelContent ? (
    <label
      data-testid="tag_options"
      htmlFor={props.id}
      className={classNames(className, styles.label, {
        [styles.disabled]: disabled,
      })}
    >
      {radioButton}
      {labelContent}
    </label>
  ) : (
    radioButton
  );
};
